// Types and helpers used by the Contracts (Contract Review) page. These mirror
// the shape produced by `mapContract` (see `./mapContract.ts`) from the real
// `Contract` returned by the API.

type ContractReviewType = string;

type CoverageType = string;

type ContractReviewRate = {
  workerType: string;
  workType: string;
  rateType: string;
  hourlyRate: number;
  coverageType: CoverageType;
  overtimeMultiplier: number | null;
  isDefault: boolean;
  description: string;
};

type ContractReviewRateSet = {
  id: string;
  name: string;
  description: string;
  rates: ContractReviewRate[];
};

type ContractReviewKpiTargets = {
  maintenanceCompletionTarget: number;
  maintenanceHoursTarget: number;
  uptimeTarget: number;
  onTimeResponseTargetPct: number;
  callbackRateTarget: number | null;
  emergencyCallRateTarget: number | null;
};

export type ContractReviewRuleCategory =
  | 'review-rule'
  | 'included-service'
  | 'excluded-service'
  | 'miscellaneous-charge'
  | 'other-term';

type ContractReviewRule = {
  id: string;
  rule: string;
  category: ContractReviewRuleCategory;
};

export type ContractReviewAttachment = {
  id: string;
  fileName: string;
  isPrimary: boolean;
  isSummarySheet: boolean;
  hasTextLayer: boolean;
  uploadedAt: string;
  // Nullable on the wire; carried as `undefined` (never '') so consumers omit
  // `href` and render an inert link rather than one pointing at the current page.
  attachmentUrl: string | undefined;
  kind: 'user_upload' | 'cfi_report';
};

export type ContractReviewCustomDetail = {
  label: string;
  value: string;
};

export type MaintenanceUnit = 'visits' | 'hours';

export type ContractReviewCarKpi = {
  carId: string;
  carName: string;
  maintenanceQuantity: number;
  maintenanceUnit: MaintenanceUnit;
  maintenanceTimePeriodMonths: number;
  regularCallbackRate: number;
  emergencyCallbackRate: number;
  regularCallbackResponseTime: number;
  emergencyCallbackResponseTime: number;
};

export type ContractReviewProperty = {
  id: string;
  propertyContractId: string;
  rateSetId: string | null;
  name: string;
  address: string;
  carCount: number;
  monthlyPrice: number;
  // Nullable on the wire (DateField(null=True)); carried through as null (never
  // '') so date consumers can render a placeholder instead of NaN — see AMP-585.
  startDate: string | null;
  expirationDate: string | null;
  cars: ContractReviewCarKpi[];
};

export type ExtractionVersion = 'v0' | 'v1' | null;

export type ContractReview = {
  id: string;
  name: string;
  vendor: string;
  type: ContractReviewType;
  properties: ContractReviewProperty[];
  // Nullable on the wire (DateField(null=True)); carried through as null (never
  // '') so date consumers can render a placeholder instead of NaN — see AMP-585.
  startDate: string | null;
  expirationDate: string | null;
  term: string;
  monthlyPriceTaxInclusive: boolean | null;
  escalationRate: number;
  cancellationDays: number | null;
  hourlyFeeAdjustmentRate: number;
  partsAndMaterialsMarkup: number;
  contractPaper: string;
  businessHours: string;
  phoneMonitoring: boolean | null;
  fireService: boolean | null;
  pressureTest: boolean | null;
  aiReviewEligible: boolean;
  /**
   * Whether the uploaded PDF has a text layer for questions to be answered from.
   * False where the contract was scanned as images, which is what the ask
   * affordance says instead of failing on every question.
   */
  canChat: boolean;
  extractionVersion: ExtractionVersion;
  extractionAppliedAt: string | null;
  reviewRules: ContractReviewRule[];
  kpiTargets: ContractReviewKpiTargets;
  rateSets: ContractReviewRateSet[];
  attachments: ContractReviewAttachment[];
  customDetails: ContractReviewCustomDetail[];
  notes: string;
};

export const formatReviewRuleCategory = (category: ContractReviewRuleCategory): string => {
  switch (category) {
    case 'review-rule':
      return 'Review rule';
    case 'included-service':
      return 'Included service';
    case 'excluded-service':
      return 'Excluded service';
    case 'miscellaneous-charge':
      return 'Misc. charge';
    case 'other-term':
      return 'Other term';
  }
};

export const formatMaintenanceTarget = (kpi: ContractReviewCarKpi): string => {
  const { maintenanceQuantity: qty, maintenanceUnit, maintenanceTimePeriodMonths: months } = kpi;
  const unitSingular = maintenanceUnit === 'visits' ? 'visit' : 'hour';
  const unitLabel = qty === 1 ? unitSingular : `${unitSingular}s`;
  const periodLabel = months === 1 ? 'month' : `${months} months`;
  return `${qty} ${unitLabel} / ${periodLabel}`;
};
